import { MIME_TYPE } from 'src/Utils/globalEnums'
import { mimeFromFilename } from 'src/Utils/mime'
import { MAX_UPLOAD_BYTES, MAX_UPLOAD_MB } from './FileSelector.constants'

// Function to correct Apple-specific MIME types based on file extensions
// correctFileTypes reconciles a browser's reported File.type with the caller's
// accepted mime list. Browsers frequently report an empty or inconsistent type
// (Apple iWork files, and .md/.txt/.rtf/.csv which often arrive with no type at
// all), so when the reported type is not accepted we fall back to the mime
// resolved from the filename extension. Files that still do not match after
// correction are dropped.
export const correctFileTypes = (
  files: File[],
  allowedMimeTypes: string[],
): File[] => {
  const setType = (file: File, value: string): void => {
    Object.defineProperty(file, 'type', { value, writable: true })
  }
  // Apple iWork extensions the browser rarely types correctly.
  const appleByExtension: Record<string, MIME_TYPE> = {
    key: MIME_TYPE.APPLE_KEYNOTE,
    numbers: MIME_TYPE.APPLE_NUMBERS,
    pages: MIME_TYPE.APPLE_PAGES,
  }

  return files
    .map((file) => {
      if (!allowedMimeTypes.includes(file.type)) {
        const extension = file.name.split('.').pop()?.toLowerCase()
        const apple = extension ? appleByExtension[extension] : undefined
        const byExtension = apple ?? mimeFromFilename(file.name)
        if (byExtension && allowedMimeTypes.includes(byExtension)) {
          setType(file, byExtension)
        }
      }
      return allowedMimeTypes.includes(file.type) ? file : null
    })
    .filter((file): file is File => file !== null)
}

// correctAppleFiles is retained as an alias for existing callers.
export const correctAppleFiles = correctFileTypes

export const stripFileExtension = (filename: string): string =>
  filename.replace(/\.[^/.]+$/, '')

export const validateFiles = (
  files: File[],
  accept: string[] = [],
  maxUploadBytes: number = MAX_UPLOAD_BYTES,
): { validFiles: File[]; errors: string | null } => {
  let errors: string | null = null

  // Filter files by size
  const oversizedFiles = files.filter((file) => file.size > maxUploadBytes)
  if (oversizedFiles.length > 0) {
    errors = `One or more files exceed the ${MAX_UPLOAD_MB}MB limit.`
  }

  // Filter files by MIME type. Browsers often report an empty or inconsistent
  // File.type (notably .md, .csv, .txt), so fall back to the mime resolved from
  // the filename extension. This only ever widens acceptance -- a file already
  // matching by File.type is unaffected.
  const validFiles = files.filter(
    (file) =>
      accept.includes(file.type) ||
      accept.includes(mimeFromFilename(file.name) ?? ''),
  )

  if (validFiles.length === 0) {
    errors = 'Invalid file type. Please upload supported files.'
  }

  return { validFiles, errors }
}
